import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const BASE = `${config.BASE_URL}/attendance`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function apiError(data, fallback) {
  if (!data || typeof data !== "object") return fallback;
  const d = typeof data.details === "string" ? data.details.trim() : "";
  const m = typeof data.message === "string" ? data.message.trim() : "";
  if (d && m) return `${m}: ${d}`;
  return d || m || fallback;
}

function periodParams({ year, month, day } = {}) {
  const p = new URLSearchParams();
  if (year != null) p.set("year", String(year));
  if (month != null) p.set("month", String(month));
  if (day != null && day !== "") p.set("day", String(day));
  return p;
}

export async function fetchMyAttendanceProfile() {
  const res = await authFetch(`${BASE}/me/profile`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load profile"));
  return data.data;
}

export async function fetchMyAttendanceHistory({ year, month, day } = {}) {
  const params = periodParams({ year, month, day });
  const res = await authFetch(`${BASE}/me/history?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load history"));
  return data.data;
}

export async function markMyAttendance({ latitude, longitude, capturePhoto, deviceId }) {
  const res = await authFetch(`${BASE}/me/mark`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      latitude,
      longitude,
      capture_photo: capturePhoto,
      device_id: deviceId ?? undefined,
    }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to mark attendance"));
  return data;
}

export async function fetchAttendanceUsers() {
  const res = await authFetch(`${BASE}/users`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load users"));
  return Array.isArray(data.data) ? data.data : [];
}

export async function fetchAttendanceReport({ userId, year, month, day } = {}) {
  const params = periodParams({ year, month, day });
  if (userId) params.set("userId", String(userId));
  const res = await authFetch(`${BASE}/reports?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load report"));
  return data.data;
}

export async function fetchMyLeaveHistory() {
  const res = await authFetch(`${BASE}/leaves/mine`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load leave history"));
  return data.data;
}

export async function applyAttendanceLeave(payload) {
  const res = await authFetch(`${BASE}/leaves`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to apply for leave"));
  return data.data;
}

export async function fetchLeaveManagerDashboard({ year, month } = {}) {
  const params = periodParams({ year, month });
  const res = await authFetch(`${BASE}/leaves/manage?${params}`, {
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to load leave applications"));
  return data.data;
}

export async function approveLeaveApplication(id) {
  const res = await authFetch(`${BASE}/leaves/${id}/approve`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to approve leave"));
  return data.data;
}

export async function rejectLeaveApplication(id, rejection_reason) {
  const res = await authFetch(`${BASE}/leaves/${id}/reject`, {
    method: "POST",
    credentials: "include",
    headers: await buildAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ rejection_reason }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(apiError(data, "Failed to reject leave"));
  return data.data;
}
